import { useEffect, useRef, useState } from 'react';
import { ModuleData, ModuleStyle } from '../../Types/containerV2';
import { getModuleTypeDefinition } from '../../Types/containerV2/moduleRegistry';
import { CSSVariables, mapStyleToBackgroundImageVars, mapStyleToVarsAndClasses } from '../../Utils/containerUtils';
import { BizIcon } from '../Mini/BizIcon';
import { useContainerContext } from './ContainerContext';
import './ModuleRenderer.scss';
import ProtectedSegment from './ProtectedSegment';

type ModuleRendererProps = {
	module: ModuleData;
};

/**
 * Dispatches rendering to the correct module component based on moduleType.
 * Uses the module registry to look up the component, so new module types
 * only need to be registered — no changes to this component are needed.
 * Unregistered or component-less module types are silently skipped.
 *
 * Wraps the module component in a div that carries width classes from
 * the module's style, enabling flex-row layout within placeholders.
 *
 * If the module has an onClick action, clicking it will perform
 * the configured action (link navigation, expand segment, or scroll to segment).
 */
const ModuleRenderer = ({ module }: ModuleRendererProps) =>
	module.moduleType === 'Segment' && module.content.passwordProtected === true ? (
		<ProtectedSegment module={module} render={(content) => <ModuleRendererContent module={content} />} />
	) : (
		<ModuleRendererContent module={module} />
	);

const ModuleRendererContent = ({ module }: ModuleRendererProps) => {
	const definition = getModuleTypeDefinition(module.moduleType);
	const { isSegmentExpanded, isClickActionEnabled, handleModuleAction, isModuleFinished, isActionDisabled, isValueSelected } =
		useContainerContext();
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const hasRevealEffect = !!module.style?.effects?.reveal?.type || !!module.mobileStyle?.effects?.reveal?.type;
	const [isEffectVisible, setIsEffectVisible] = useState(!hasRevealEffect);

	useEffect(() => {
		if (!hasRevealEffect) {
			setIsEffectVisible(true);
			return;
		}

		// Reset so the reveal animation can run when the effect is enabled after mount.
		setIsEffectVisible(false);

		const element = wrapperRef.current;
		if (!element) {
			setIsEffectVisible(true);
			return;
		}

		if (!('IntersectionObserver' in window)) {
			setIsEffectVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsEffectVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.15 }
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, [hasRevealEffect]);

	if (!definition?.component) {
		return null;
	}

	const { vars, classes } = mapStyleToVarsAndClasses(module.style, module.mobileStyle);
	classes.unshift('biz-module-wrapper'); // Ensure base class is first for easier overrides
	if (definition.wrapperClasses?.length) {
		classes.push(...definition.wrapperClasses);
	}

	if (module.cssClasses) {
		classes.push(...module.cssClasses);
	}

	if (hasRevealEffect && isEffectVisible) {
		classes.push('biz-module-wrapper--effect-visible');
	}

	const hasClickAction = !!module.onClick && !(module.onClick.type === 'finish-module' && isModuleFinished(module.id));
	if (hasClickAction) {
		classes.push('biz-module-wrapper--clickable');
	}

	const isExpandAction = module.onClick?.type === 'expand-segment';
	const isTargetExpanded = isExpandAction
		? isSegmentExpanded((module.onClick as { type: 'expand-segment'; segmentGuid: string }).segmentGuid)
		: false;
	const isActiveClickAction = !!module.onClick && isClickActionEnabled(module.onClick.type);
	const isSelection = module.onClick?.type === 'select-value';
	const isDisabled = !!module.onClick && (isActionDisabled(module.onClick) || (isSelection && module.moduleType !== 'Button'));
	const isSelected = module.onClick?.type === 'select-value' && isValueSelected(module.onClick.group, module.onClick.value);
	if (isSelected) classes.push('biz-module-wrapper--selected');
	if (isDisabled) classes.push('biz-module-wrapper--disabled');

	const handleClick = hasClickAction
		? (e?: { preventDefault: () => void; stopPropagation: () => void }) => {
				if (isActiveClickAction) {
					e?.preventDefault();
					e?.stopPropagation();
				}
				if (!isDisabled) handleModuleAction(module.onClick!);
			}
		: undefined;
	const interactiveRole = module.onClick?.type === 'link' ? 'link' : 'button';

	const Component = definition.component;
	return (
		<div
			ref={wrapperRef}
			className={classes.join(' ')}
			style={vars}
			data-module-guid={module.guid}
			onClick={handleClick}
			role={hasClickAction ? interactiveRole : undefined}
			aria-pressed={isSelection ? isSelected : undefined}
			aria-disabled={hasClickAction && isDisabled ? true : undefined}
			tabIndex={hasClickAction ? 0 : undefined}
			onKeyDown={
				handleClick
					? (e) => {
							if (e.key === 'Enter' || (interactiveRole !== 'link' && e.key === ' ')) {
								e.preventDefault();
								handleClick(e);
							}
						}
					: undefined
			}
		>
			{renderBackgroundImage(module.style, module.mobileStyle)}
			{renderExpandIcon(isExpandAction, isTargetExpanded)}
			<Component module={module} />
		</div>
	);
};

function renderExpandIcon(isExpandAction: boolean, isTargetExpanded: boolean) {
	if (!isExpandAction) return null;
	return <BizIcon icon={isTargetExpanded ? 'CHEVRON-DOWN' : 'CHEVRON-RIGHT'} className='expand-icon' />;
}

function renderBackgroundImage(style?: ModuleStyle, mobileStyle?: ModuleStyle) {
	if (!style?.background?.imageUrl && !mobileStyle?.background?.imageUrl) return null;
	const mobileVars = mapStyleToBackgroundImageVars({
		background: {
			...style?.background,
			...Object.fromEntries(Object.entries(mobileStyle?.background ?? {}).filter(([, value]) => value !== undefined)),
		},
	});
	const vars: CSSVariables = {
		...Object.fromEntries(
			Object.entries(mapStyleToBackgroundImageVars(style)).map(([key, value]) => [`${key}-desktop`, value])
		),
		...Object.fromEntries(Object.entries(mobileVars).map(([key, value]) => [`${key}-mobile`, value])),
	};
	return <div className='biz-module-wrapper-background-image' style={vars} />;
}

export default ModuleRenderer;
